import { DownOutlined } from '@ant-design/icons';
import { Dropdown, message, Switch } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { connect } from 'react-redux';
import {
	addBuyerSubscribersForBuyers,
	addSupplierSubscribersForSuppliers,
	removeBuyerSubscriberForBuyers,
	removeSupplierSubscriberForSuppliers,
} from '../../thunks/work_orders_subscribers_thunks';
import { USER_NOTIFICATIONS_CRUD_ACTION_CREATORS } from '../../actions/user_notifications_actions';
import {
	getNotificationDetailsFromKey,
	getNotificationLabelFromDetails,
	getSubscribeObjectForWorkOrder,
} from '../../utils/NotificationUtils';
import { buyerSettingsRestCrudThunksForBuyer } from '../../thunks/buyer_settings_thunks';
import { supplierSettingsRestCrudThunksForSupplier } from '../../thunks/supplier_settings_thunks';

const EachNotificationSetting = ({
	title,
	saving,
	description,
	isSubscribed,
	onChange,
	disabled = false,
	workOrder,
}) => {
	const modifiedIsSubscribed = useMemo(
		() =>
			//When the WO notificaiton is turned off, others will be disabled
			//When it is disabled it should be turned off as well
			disabled ? false : isSubscribed,
		[disabled, isSubscribed]
	);

	const isPrivateWorkOrderAndUnsubscribed = useMemo(
		() => nullSafeGet('isPrivate', workOrder) && !isSubscribed,
		[isSubscribed, workOrder]
	);

	const revisedDisabled = useMemo(
		() => disabled || isPrivateWorkOrderAndUnsubscribed,
		[disabled, isPrivateWorkOrderAndUnsubscribed]
	);

	return (
		<div
			className={`flex w-full flex-row items-center justify-between ${
				revisedDisabled ? 'opacity-60' : ''
			}`}
		>
			<div className="flex flex-col items-start">
				<div className="bodymedium">
					{title}
					{/* {isPrivateWorkOrderAndUnsubscribed ? <span className='bodyregular ml-2 opacity-45'>{`(Private)`}</span> : null} */}
				</div>
				<div className="bodyregular mt-2 opacity-45">{description}</div>
			</div>
			<div className="ml-6 flex min-w-[80px] flex-col items-center justify-center">
				<div
					className={`footnoteMedium`}
					style={{ color: modifiedIsSubscribed ? '#389E0D' : '#FF4D4F' }}
				>
					{modifiedIsSubscribed ? 'Subscribed' : 'Unsubscribed'}
				</div>
				<div onClick={stopDefaultEvents}>
					<Switch
						size="small"
						loading={saving}
						disabled={revisedDisabled}
						checked={modifiedIsSubscribed}
						onChange={onChange}
					/>
				</div>
			</div>
		</div>
	);
};

const NOTIFICATION_TYPE = {
	email: 'email',
	inApp: 'inApp',
	workOrder: 'workOrder',
};

const NotificationSubscriptionHandler: FC<any> = ({
	notification,
	userType,
	currentUser,
	subscribeToWO,
	unsubscribeFromWO,
	updateNotificaitonObjectInRedux,
	settings,
	updateSettings,
}): React.ReactElement => {
	const [saving, setSaving] = useState(null);

	const woAvailable = useMemo(() => !!nullSafeGet('workOrder', notification), [notification]);

	const subscriberList = useMemo(() => {
		return userType === ROLE_TYPES.SUPPLIER
			? nullSafeGetOrElse('workOrder.supplierSubscriberEmails', notification, [])
			: nullSafeGetOrElse('workOrder.buyerSubscriberEmails', notification, []);
	}, [notification, userType]);

	const isWOSubscribed = useMemo(
		() => subscriberList.includes(nullSafeGetOrElse('email', currentUser, '')),
		[currentUser, subscriberList]
	);

	const subscribeObj = useMemo(
		() =>
			getSubscribeObjectForWorkOrder(
				isWOSubscribed,
				nullSafeGet('workOrder.id', notification),
				currentUser
			),
		[currentUser, isWOSubscribed, notification]
	);

	const onSubscribe = useCallback(() => {
		setSaving(NOTIFICATION_TYPE.workOrder);
		subscribeToWO(subscribeObj, nullSafeGet('workOrder', notification))
			.then((workOrder) =>
				updateNotificaitonObjectInRedux({
					...notification,
					workOrder,
				})
			)
			.catch(message.error)
			.finally(() => setSaving(null));
	}, [subscribeToWO, subscribeObj, notification, updateNotificaitonObjectInRedux]);

	const onUnSubscribe = useCallback(() => {
		setSaving(NOTIFICATION_TYPE.workOrder);
		unsubscribeFromWO(subscribeObj, nullSafeGet('workOrder', notification))
			.then((workOrder) =>
				updateNotificaitonObjectInRedux({
					...notification,
					workOrder,
				})
			)
			.catch(message.error)
			.finally(() => setSaving(null));
	}, [notification, subscribeObj, unsubscribeFromWO, updateNotificaitonObjectInRedux]);

	const notificationDetails = useMemo(
		() => getNotificationDetailsFromKey(userType)(nullSafeGet('notificationType', notification)),
		[notification, userType]
	);

	const existingRootNotificationConfig = useMemo(
		() =>
			notificationDetails && nullSafeGetOrElse(notificationDetails.notificationKey, settings, {}),
		[settings, notificationDetails]
	);

	const existingNotificationConfig = useMemo(
		() =>
			notificationDetails && nullSafeGet(notificationDetails.key, existingRootNotificationConfig),
		[existingRootNotificationConfig, notificationDetails]
	);

	const emailSubscribed = useMemo(
		() => (existingNotificationConfig ? existingNotificationConfig.email !== false : true),
		[existingNotificationConfig]
	);

	const inAppSubscribed = useMemo(
		() => (existingNotificationConfig ? existingNotificationConfig.inApp !== false : true),
		[existingNotificationConfig]
	);

	const handleBuyerSettingsChange = useCallback(
		(subscriptionType, value) => {
			const newSettings = {
				...settings,
				[notificationDetails.notificationKey]: {
					...existingRootNotificationConfig,
					[notificationDetails.key]: {
						...(existingNotificationConfig || {}),
						[subscriptionType]: value,
					},
				},
			};
			setSaving(subscriptionType);
			updateSettings(newSettings)
				.catch(message.error)
				.finally(() => setSaving(null));
		},
		[
			settings,
			existingNotificationConfig,
			existingRootNotificationConfig,
			notificationDetails,
			updateSettings,
		]
	);

	const onSubscriptionToggle = useCallback(
		(type) => (value) => {
			switch (type) {
				case NOTIFICATION_TYPE.workOrder:
					value ? onSubscribe() : onUnSubscribe();
					break;
				default:
					handleBuyerSettingsChange(type, value);
					break;
			}
		},
		[handleBuyerSettingsChange, onSubscribe, onUnSubscribe]
	);

	const notificationLabel = useMemo(
		() =>
			notificationDetails &&
			`Notifications for ${getNotificationLabelFromDetails(notificationDetails)}`,
		[notificationDetails]
	);

	const notificationStatusProps = useMemo(() => {
		if (!isWOSubscribed)
			return {
				label: 'WO notifications OFF',
				iconName: 'notification_bell_with_strike',
				iconColor: '#FF4D4F',
			};

		return emailSubscribed && inAppSubscribed
			? {
					label: 'All notifications ON',
					iconName: 'notification_bell_with_tick',
					iconColor: '#389E0D',
			  }
			: emailSubscribed
			? {
					label: 'Email ON, Mobile Push OFF',
					iconName: 'notification_email_with_tick',
					iconColor: '#389E0D',
			  }
			: inAppSubscribed
			? {
					label: 'Mobile Push ON, Email OFF',
					iconName: 'notification_mobile_with_tick',
					iconColor: '#389E0D',
			  }
			: {
					label: 'Email & Mobile Push OFF',
					iconName: 'notification_mobile_cancel',
					icon2Name: 'notification_email_cancel',
					iconColor: '#FA8C16',
			  };
	}, [emailSubscribed, inAppSubscribed, isWOSubscribed]);

	const DropdownContect = useMemo(() => {
		return (
			<div className="flex flex-col items-start gap-4 bg-white p-4 shadow-notifications-card">
				{notificationLabel ? (
					<>
						<EachNotificationSetting
							title="Email"
							description={notificationLabel}
							isSubscribed={emailSubscribed}
							saving={saving === NOTIFICATION_TYPE.email}
							disabled={!isWOSubscribed}
							onChange={onSubscriptionToggle(NOTIFICATION_TYPE.email)}
						/>
						<EachNotificationSetting
							title="Mobile Push"
							description={notificationLabel}
							isSubscribed={inAppSubscribed}
							saving={saving === NOTIFICATION_TYPE.inApp}
							disabled={!isWOSubscribed}
							onChange={onSubscriptionToggle(NOTIFICATION_TYPE.inApp)}
						/>
					</>
				) : null}
				{woAvailable ? (
					<EachNotificationSetting
						title={`WO #${nullSafeGetOrElse('workOrder.id', notification, '--')}`}
						description="Email & mobile push notifications for this work order"
						isSubscribed={isWOSubscribed}
						workOrder={nullSafeGet('workOrder', notification)}
						saving={saving === NOTIFICATION_TYPE.workOrder}
						onChange={onSubscriptionToggle(NOTIFICATION_TYPE.workOrder)}
					/>
				) : null}
			</div>
		);
	}, [
		emailSubscribed,
		inAppSubscribed,
		isWOSubscribed,
		notification,
		notificationLabel,
		onSubscriptionToggle,
		saving,
		woAvailable,
	]);

	const getIconElement = useCallback(
		(iName, iColor) => (
			<div
				className="my-1 h-6 w-6 bg-cover bg-center"
				style={{
					WebkitMaskImage: `url('/${iName}.svg')`,
					WebkitMaskRepeat: 'no-repeat',
					WebkitMaskSize: 'contain',
					WebkitMaskPosition: 'center',
					maskImage: `url('/${iName}.svg')`,
					maskRepeat: 'no-repeat',
					maskSize: 'contain',
					maskPosition: 'center',
					backgroundColor: iColor,
				}}
			/>
		),
		[]
	);

	return (
		<Dropdown overlay={DropdownContect} className="ml-4" trigger={['click']}>
			<div key={1} onClick={stopDefaultEvents} className="border-1 group">
				<div className="flex flex-row items-center gap-2 rounded-md border border-solid border-[#D9D9D9] p-2">
					{getIconElement(notificationStatusProps.iconName, notificationStatusProps.iconColor)}
					{notificationStatusProps.icon2Name
						? getIconElement(notificationStatusProps.icon2Name, notificationStatusProps.iconColor)
						: null}
					<span className="bodymedium hidden flex-nowrap whitespace-nowrap group-hover:inline-flex ">
						{notificationStatusProps.label}
					</span>
					<DownOutlined className="h-4" />
				</div>
			</div>
		</Dropdown>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	settings:
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? state.supplier_settings.detail
			: state.buyer_settings.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	subscribeToWO: (obj, workOrder) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? addSupplierSubscribersForSuppliers(obj)(workOrder)
				: addBuyerSubscribersForBuyers(obj)(workOrder)
		),
	unsubscribeFromWO: (obj, workOrder) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? removeSupplierSubscriberForSuppliers(obj)(workOrder)
				: removeBuyerSubscriberForBuyers(obj)(workOrder)
		),
	updateNotificaitonObjectInRedux: (data) =>
		dispatch(USER_NOTIFICATIONS_CRUD_ACTION_CREATORS.updateSuccess(data)),
	updateSettings: (settings) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierSettingsRestCrudThunksForSupplier.update(settings)
				: buyerSettingsRestCrudThunksForBuyer.update(settings)
		),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(NotificationSubscriptionHandler)
);
